<template>
  <div class="info-box-container mt-10">
    <div class="join-us-above justify-center"><h2 class="text-4xl md:text-6xl font-bold text-base-content">Types <span class="text-secondary font-bold text-shadow-lg">of</span> Trailers</h2></div>
    
    <div class="flex flex-wrap justify-center gap-6 w-full max-w-[1300px] mx-auto p-5">
      <div
        class="info-box"
        v-for="(box, index) in boxes"
        :key="index"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave($event, index)"
        @mousemove="handleMouseMove($event, index)"
        @click="navigateTo(box.link)"
      >
        <img class="info-box-image grayscale" :src="box.grayscaleImage" :alt="`${box.title} grayscale image`" />
        <img class="info-box-image color" :src="box.colorImage" :alt="`${box.title} color image`" />
        <div class="info-box-content">
          <h3>{{ box.title }}</h3>
          <p>{{ box.description }}</p>
          <div class="more-info">
            <span class="more-info-text">More Info &ensp;</span>
            <font-awesome-icon :icon="['fas', 'right-long']" class="arrow-icon" />
          </div>
        </div>
      </div>
    </div>

    <!-- In-stock trailers: opens the same Available Stock popup as the ticker -->
    <button
      v-if="hasAvailableStock"
      type="button"
      class="stock-link"
      @click="openStock"
    >
      <span class="stock-link-dot" aria-hidden="true"></span>
      <span class="stock-link-lead">Need a trailer now?</span>
      <span class="stock-link-text">Browse our in-stock inventory</span>
      <font-awesome-icon :icon="['fas', 'right-long']" class="stock-link-arrow" />
    </button>
  </div>
</template>

<script>
import { hasAvailableStock, openStockViewer } from '@/utils/stockViewer.js';

export default {
  name: 'InfoBoxGrid',
  data() {
    return {
      hasAvailableStock,
      boxes: [
        {
          title: '3-A Sanitary',
          description: 'Our nation’s milk supply must be safely transported from farms to processing plants in purpose-built, clean and temperature-controlled trailers.  Our standard size models start at 6,200 gallons and can go up to 10,500 gallons.',
          grayscaleImage: '/img/thumbnail/Sanitary3A_small.png',
          colorImage: '/img/thumbnail/Sanitary3A_small.png',
          link: { path: '/trailers', hash: '#slide2' },
        },
        {
          title: 'Food Grade',
          description: 'Liquid chocolate, cooking oils, sweeteners, juices, wine and even bourbon require consistent and uniform temperatures to prevent damage to the product while in transit.  Our proven solutions will provide you with the peace-of-mind that you want.',
          grayscaleImage: '/img/thumbnail/FoodGrade_small.png',
          colorImage: '/img/thumbnail/FoodGrade_small.png',
          link: { path: '/trailers', hash: '#slide3' },
        },
        {
          title: 'DOT407 Chemical',
          description: 'Chemicals used in the manufacturing of everyday household products are often transported hot from chemical manufacturer to finished product manufacturer.  Our DOT407 certified models start at 5,000 gallons and can be customized for your specific uses.',
          grayscaleImage: '/img/thumbnail/Chemical_small.png',
          colorImage: '/img/thumbnail/Chemical_small.png',
          link: { path: '/trailers', hash: '#slide4' },
        },
        {
          title: 'ASME',
          description: 'More hazardous chemicals and other industrial by-products require an extra degree of control over the quality of the vessel.  Our ASME certification procedures are consistently audited for compliance and provide our customers with confidence.',
          grayscaleImage: '/img/thumbnail/ASME_small.png',
          colorImage: '/img/thumbnail/ASME_small.png',
          link: { path: '/trailers', hash: '#slide5' },
        },
        {
          title: 'Double Conical',
          description: 'Some chemical and most farming applications require a double conical tank design for center, bottom discharge.  We build a few models, in a variety of sizes and configurations and with optional insulation.',
          grayscaleImage: '/img/thumbnail/Fertilizer_small.png',
          colorImage: '/img/thumbnail/Fertilizer_small.png',
          link: { path: '/trailers', hash: '#slide6' },
        },
      ],
      isAnimating: false, // Add this to control animation frame rate
      boxRefs: [],
      activeBoxIndex: null
    };
  },
  mounted() {
    // Initialize box references
    this.$nextTick(() => {
      this.boxRefs = Array(this.boxes.length).fill(null);
    });
  },
  methods: {
    openStock() {
      openStockViewer();
    },
    navigateTo(link) {
      if (typeof link === 'object') {
        this.$router.push(link);
      } else {
        window.location.href = link; // Fallback for non-Vue routes
      }
    },
    handleMouseEnter(event) {
      const box = event.currentTarget;
      box.classList.add('hovered');
    },
    handleMouseLeave(event, index) {
      const box = event.currentTarget;
      box.classList.remove('hovered');
      
      // Save a reference to the current box
      this.boxRefs[index] = box;
      this.activeBoxIndex = null;
      
      // Reset transforms with a small delay to ensure smooth transition
      requestAnimationFrame(() => {
        if (this.boxRefs[index]) {
          // Reset the main box transform
          this.boxRefs[index].style.transform = '';
          
          // Reset all images inside the box
          const images = this.boxRefs[index].querySelectorAll('.info-box-image');
          images.forEach(img => {
            img.style.transform = '';
          });
        }
      });
    },
    handleMouseMove(event, index) {
      // Store a reference to the current target element before the async callback
      const box = event.currentTarget;
      this.boxRefs[index] = box; // Store reference to box
      this.activeBoxIndex = index;
      
      // Save event properties we'll need
      const x = event.clientX;
      const y = event.clientY;
      
      // Debounce animation frames
      if (this.isAnimating) return;
      this.isAnimating = true;
      
      // Use requestAnimationFrame for smoother animations
      requestAnimationFrame(() => {
        // Get the stored element reference
        const currentBox = this.boxRefs[index];
        
        // Make sure the element still exists and we're still hovering this box
        if (!currentBox || this.activeBoxIndex !== index) {
          this.isAnimating = false;
          return;
        }
        
        const rect = currentBox.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        // Calculate relative position to center
        const relX = x - rect.left;
        const relY = y - rect.top;
        
        const percentX = (relX - centerX) / centerX;
        const percentY = (relY - centerY) / centerY;
        
        // Limit rotation to reduce jittering
        const rotateY = Math.min(Math.max(-percentX * 20, -20), 20); 
        const rotateX = Math.min(Math.max(percentY * 20, -20), 20);
        
        currentBox.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;

        // Parallax effect with limited movement
        const imageRotateY = Math.min(Math.max(percentX * 10, -10), 10);
        const imageRotateX = Math.min(Math.max(-percentY * 10, -10), 10);
        
        currentBox.querySelectorAll('.info-box-image').forEach(img => {
          img.style.transform = `scale(1.2) translateX(-40%) perspective(1000px) rotateX(${imageRotateX}deg) rotateY(${imageRotateY}deg)`;
        });
        
        this.isAnimating = false;
      });
    },
  },
};
</script>

<style scoped>
.header {
  font-size: 7vh;
  font-weight: bold;
  margin: auto;
  color: #fff;

  @media (max-height: 964px) {
    font-size: 2rem;
  }
}

.join-us-above {
  position: relative;
  width: 100%;
  margin-top: 5vh;
  margin-bottom: 1vh;
  display: flex;
}

.info-box-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
}

.info-box {
  background: radial-gradient(circle at bottom, #fff 50%, #2e2f3b 90%);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-out, background-color 0.3s ease;
  cursor: pointer;
  margin: 85px 0 20px 0;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  filter: drop-shadow(20px 20px 10px #1d1e26);
  z-index: 14;
  position: relative;
  width: 375px;
  will-change: transform; /* Optimize for animation performance */
}

.info-box-image {
  position: absolute;
  top: -25%;
  left: 50%;
  transform: translateX(-50%);
  width: 75%;
  max-width: 75%;
  z-index: 25;
  filter: drop-shadow(20px 20px 10px #1d1e26);
  transition: transform 0.3s ease-out;
  will-change: transform; /* Optimize for animation performance */
}

.info-box.hovered .info-box-image.color {
  display: block;
  transform: scale(1.2) translateX(-40%);
}

.info-box-content {
  padding: 20px;
  text-align: left;
  position: relative;
  z-index: 15;
  margin-top: 50px;
}

.info-box h3 {
  color: #000;
  font-weight: 600;
  font-size: 1.5rem;
  padding-bottom: 15px;
}

.info-box p {
  color: #000;
  line-height: 1.2rem;
}

.more-info {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  transition: all 0.3s ease;
  font-weight: 600;
  padding: 10px 20px;
}

.arrow-icon {
  font-size: 1.5rem;
  margin-left: 10px;
}

.more-info-text {
  display: none;
}

.info-box.hovered {
  background: radial-gradient(ellipse at center, #8f0000 0%, #a22a21 90%);
  background-color: #a22a21;
  color: white;
  filter: drop-shadow(20px 20px 10px #1d1e26);
}

.info-box.hovered h3,
.info-box.hovered p {
  color: white;
}

.info-box.hovered .more-info {
  background-color: white;
  color: #a22a21;
  border-radius: 20px;
}

.info-box.hovered .more-info-text {
  display: inline-block;
}

/* "Browse our in-stock inventory" text link below the grid */
.stock-link {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.35rem 0.6rem;
  margin: 1.5rem auto 1rem;
  padding: 0.5rem 0.75rem;
  font-size: 1.125rem;
  line-height: 1.4;
  color: var(--color-secondary);
  background: transparent;
  border: none;
  cursor: pointer;
}

.stock-link-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff3b3b;
  box-shadow: 0 0 8px rgba(255, 59, 59, 0.8);
  animation: stock-link-pulse 2s ease-in-out infinite;
}

.stock-link-text {
  font-weight: 600;
  color: var(--color-base-content);
  text-decoration: underline;
  text-decoration-color: #8f0000;
  text-decoration-thickness: 2px;
  text-underline-offset: 5px;
  transition: color 0.25s ease, text-decoration-color 0.25s ease;
}

.stock-link-arrow {
  color: var(--color-base-content);
  transition: transform 0.25s ease, color 0.25s ease;
}

.stock-link:hover .stock-link-text,
.stock-link:focus-visible .stock-link-text {
  color: #ff6a6a;
  text-decoration-color: #ff6a6a;
}

.stock-link:hover .stock-link-arrow,
.stock-link:focus-visible .stock-link-arrow {
  color: #ff6a6a;
  transform: translateX(4px);
}

@keyframes stock-link-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.85); }
}

@media (prefers-reduced-motion: reduce) {
  .stock-link-dot {
    animation: none;
  }
}

/* For smaller screens, adjust the size slightly */
@media (max-width: 400px) {
  .info-box {
    width: 280px;
  }
}

/* Touch device override for hover effects */
@media (hover: none) {
  .info-box-image {
    transform: translateX(-50%) !important;
  }
  
  .info-box:active {
    background: radial-gradient(ellipse at center, #8f0000 0%, #a22a21 90%);
    color: white;
  }
  
  .info-box:active h3,
  .info-box:active p {
    color: white;
  }
  
  .info-box:active .more-info {
    background-color: white;
    color: #a22a21;
    border-radius: 20px;
  }
  
  .info-box:active .more-info-text {
    display: inline-block;
  }
}
</style>